import React, { useMemo } from 'react';
import {
  IonBackButton,
  IonButtons,
  IonButton,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonTitle,
  IonToolbar,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import { useHistory, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useRecipes } from '../hooks/useRecipes';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { CreateRecipeDto, IProcessedMaterial, IRawMaterial } from 'shared';
import apiClient from '../api/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface Params {
  id: string;
}

interface IngredientFormRow {
  id?: string;
  key: string;
  materialId: string;
  materialType: 'processed' | 'raw';
  unit: string;
  quantityNeeded: number;
}

interface FormValues {
  ingredients: IngredientFormRow[];
}

const convertUnit = (quantity: number, fromUnit: string, toUnit: string): number => {
  if (fromUnit === toUnit) return quantity;
  let baseQuantity = quantity;
  if (fromUnit === 'kg' || fromUnit === 'l') baseQuantity *= 1000;
  if (toUnit === 'kg' || toUnit === 'l') return baseQuantity / 1000;
  return baseQuantity;
};

const RecipeEdit: React.FC = () => {
  const { id } = useParams<Params>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const { recipes, menuItems, processedMaterials, rawMaterials } = useRecipes();
  const menuItem = menuItems.find((item) => item.id === id);

  const existingRecipes = useMemo(
    () => recipes.filter((recipe) => recipe.menuItemId === id),
    [recipes, id],
  );

  const processedMap = useMemo(() => {
    const map = new Map<string, IProcessedMaterial>();
    processedMaterials.forEach((material) => map.set(material.id, material));
    return map;
  }, [processedMaterials]);

  const rawMap = useMemo(() => {
    const map = new Map<string, IRawMaterial>();
    rawMaterials.forEach((material) => map.set(material.id, material));
    return map;
  }, [rawMaterials]);

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      ingredients:
        existingRecipes.length > 0
          ? existingRecipes.map((recipe, index) => {
              const material =
                processedMap.get(recipe.processedMaterialId || '') ||
                rawMap.get(recipe.rawMaterialId || '');
              return {
                id: recipe.id,
                key: `existing-${index}`,
                materialId: material?.id || '',
                materialType: recipe.processedMaterialId ? 'processed' : 'raw',
                unit: material?.unit || '',
                quantityNeeded: Number(recipe.quantityNeeded || 0),
              };
            })
          : [{ key: 'new-0', materialId: '', materialType: 'processed', unit: '', quantityNeeded: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ingredients',
  });

  const createMutation = useMutation({
    mutationFn: async (payload: CreateRecipeDto[]) => {
      await Promise.all(payload.map((dto) => apiClient.post('/recipes', dto)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ toDelete, toUpdate, toCreate }: {
      toDelete: string[];
      toUpdate: { id: string; dto: Partial<CreateRecipeDto> }[];
      toCreate: CreateRecipeDto[];
    }) => {
      await Promise.all(toDelete.map((recipeId) => apiClient.delete(`/recipes/${recipeId}`)));
      await Promise.all(
        toUpdate.map(({ id: recipeId, dto }) => apiClient.put(`/recipes/${recipeId}`, dto)),
      );
      await Promise.all(toCreate.map((dto) => apiClient.post('/recipes', dto)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
  });

  if (!menuItem) {
    history.replace('/tabs/recipes');
    return null;
  }

  const onSubmit = async (values: FormValues) => {
    const validRows = values.ingredients.filter((row) => row.materialId && row.quantityNeeded > 0);
    if (validRows.length === 0) {
      return;
    }

    const payloads = validRows.map((row) => {
      const material = processedMap.get(row.materialId) || rawMap.get(row.materialId);
      const baseUnit = material?.unit || row.unit;
      const quantity = row.unit && baseUnit
        ? convertUnit(row.quantityNeeded, row.unit, baseUnit)
        : row.quantityNeeded;

      return {
        menuItemId: id,
        ...(row.materialType === 'processed'
          ? { processedMaterialId: row.materialId }
          : { rawMaterialId: row.materialId }),
        quantityNeeded: quantity,
      } as CreateRecipeDto;
    });

    const toDelete = existingRecipes
      .filter((recipe) => !validRows.some((row) => row.id === recipe.id))
      .map((recipe) => recipe.id);

    const toUpdate = validRows
      .filter((row) => row.id)
      .map((row) => {
        const recipeId = row.id as string;
        const dto = payloads.find((payload) =>
          (payload.processedMaterialId && payload.processedMaterialId === row.materialId) ||
          (payload.rawMaterialId && payload.rawMaterialId === row.materialId)
        );
        return { id: recipeId, dto };
      })
      .filter((entry): entry is { id: string; dto: CreateRecipeDto } => Boolean(entry.dto));

    const toCreate = payloads.filter((payload) =>
      !validRows.some((row) => row.id && row.materialId === (payload.processedMaterialId || payload.rawMaterialId)),
    );

    if (existingRecipes.length > 0) {
      await updateMutation.mutateAsync({ toDelete, toUpdate, toCreate });
    } else {
      await createMutation.mutateAsync(payloads);
    }

    history.replace(`/tabs/recipes/${id}`);
  };

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/tabs/recipes/${id}`} />
          </IonButtons>
          <IonTitle>{t('recipes')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <form onSubmit={handleSubmit(onSubmit)}>
          <IonList>
            {fields.map((field, index) => (
              <IonItem key={field.id} lines="full">
                <IonLabel position="stacked">{t('material')}</IonLabel>
                <Controller
                  control={control}
                  name={`ingredients.${index}.materialId`}
                  rules={{ required: true }}
                  render={({ field: materialField }) => (
                    <IonSelect
                      value={materialField.value}
                      placeholder={t('selectMaterial') ?? 'Select material'}
                      onIonChange={(event) => materialField.onChange(event.detail.value)}
                    >
                      {processedMaterials.map((material) => (
                        <IonSelectOption key={material.id} value={material.id}>
                          {material.name}
                        </IonSelectOption>
                      ))}
                      {rawMaterials.map((material) => (
                        <IonSelectOption key={material.id} value={material.id}>
                          {material.name}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  )}
                />

                <IonLabel position="stacked">{t('quantity')}</IonLabel>
                <Controller
                  control={control}
                  name={`ingredients.${index}.quantityNeeded`}
                  rules={{ required: true, min: 0 }}
                  render={({ field: quantityField }) => (
                    <IonInput
                      type="number"
                      value={quantityField.value?.toString() ?? ''}
                      onIonChange={(event) => quantityField.onChange(Number(event.detail.value ?? 0))}
                    />
                  )}
                />

                <IonButtons slot="end">
                  <IonButton
                    type="button"
                    color="danger"
                    onClick={() => remove(index)}
                    disabled={fields.length === 1}
                  >
                    {t('removeRow') ?? 'Remove'}
                  </IonButton>
                </IonButtons>
              </IonItem>
            ))}
          </IonList>

          <IonButton
            type="button"
            expand="block"
            onClick={() => append({ key: `new-${Date.now()}`, materialId: '', materialType: 'processed', unit: '', quantityNeeded: 0 })}
          >
            {t('addIngredients')}
          </IonButton>

          <IonButton
            type="submit"
            expand="block"
            style={{ marginTop: 24 }}
          >
            {t('save')}
          </IonButton>
        </form>
      </IonContent>
    </IonPage>
  );
};

export default RecipeEdit;
